"use client";
import styled from "@emotion/styled";
import {
  Container,
  Grid,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
} from "@mui/material";
import { useCreatemessageMutation } from "@/redux/api/message";
import * as Yup from "yup";
import React, { useState } from "react";
import { useFormik } from "formik";
import { Phone, Email, LocationOn, Language } from "@mui/icons-material";
import Header from "../home/Header";
import Footer from "../home/Footer";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

const PRIMARY_COLOR = "#635BFF";
const PageWrapper = styled.div`
  background-color: #ffffff;
  min-height: 100vh;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
`;

const SectionContainer = styled(Container)`
  padding: 64px 20px;
`;

const Title = styled(Typography)`
  font-weight: 700;
  color: ${PRIMARY_COLOR};
  margin-bottom: 16px;
`;

const Subtitle = styled(Typography)`
  color: #555;
  margin-bottom: 40px;
`;

const ContactCard = styled(Paper)`
  padding: 24px;
  border-radius: 16px;
  box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: flex-start;
  gap: 16px;
  background-color: #f9f9f9;
`;

const StyledForm = styled.form`
  padding: 32px;
  border-radius: 16px;
  background-color: #f5f5ff;
  box-shadow: 0px 8px 24px rgba(99, 91, 255, 0.1);
`;

const StyledButton = styled(Button)`
  background-color: ${PRIMARY_COLOR};
  color: #fff;
  font-weight: 600;
  margin-top: 16px;

  &:hover {
    background-color: #534bdb;
  }
`;

export default function ContactUs() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [createMessage] = useCreatemessageMutation();

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      subject: "",
      message: "",
      mobile_no:"",
    },
    validationSchema: Yup.object().shape({
      name: Yup.string().required("Please input the name!"),
      email: Yup.string()
        .email("Invalid email format")
        .required("Please input the email!"),
      subject: Yup.string().optional(), 
      message: Yup.string().required("Please input a message!"),
      mobile_no: Yup.string()
      .required("Please input the phone number!")
      .matches(/^[0-9]{10}$/, "Phone number must be exactly 10 digits"),
      }),
    onSubmit: async (values) => {
      const payload = {
        name: values.name,
        email: values.email,
        subject: values.subject,
        message: values.message,
        mobile_no: values.mobile_no,
      };

      try {
        await createMessage(payload).unwrap();
        setFormSubmitted(true);
        formik.resetForm();
      } catch (error) {
        console.error("Message submission failed:", error);
      }
    },
  });

  const { setValues } = formik;

  const handleSubmitForm = async () => {
    console.log("Form values:", formik.values); 
    formik.setTouched({
      name: true,
      email: true,
      subject: true,
      message: true,
      mobile_no:true,

    });

    formik.validateForm().then((errors) => {
      if (Object.keys(errors).length === 0) {
        const payload = {
          name: formik.values.name,
          email: formik.values.email,
          subject: formik.values.subject,
          message: formik.values.message,
          mobile_no:formik.values.mobile_no,
        };

        console.log("Payload:", payload); // Log to see if the data is correct

        createMessage(payload)
          .unwrap()
          .then((response) => {
            console.log("API response:", response); // Log the success response
            setFormSubmitted(true);
            formik.resetForm();
          })
          .catch((error) => {
            console.error("Error submitting message:", error); // Log any errors
          });
      } else {
        console.log("Form errors:", errors); // Log form errors
      }
    });
  };

  return (
    <PageWrapper>
      <Header />
      <SectionContainer maxWidth="lg">
        <Title variant="h4">Contact Us</Title>
        <Subtitle variant="body1">
          We&rsquo;d love to hear from you! Fill out the form or reach us
          through the contact details below.
        </Subtitle>

        <Grid container spacing={5}>
          <Grid item xs={12} md={6}>
            <Box display="flex" flexDirection="column" gap={3}>
              <ContactCard>
                <LocationOn sx={{ color: PRIMARY_COLOR, fontSize: 32 }} />
                <Box>
                  <Typography fontWeight={600}>Address</Typography>
                  <Typography variant="body2">
                    C1-303, Pragati IT Park, Opp. AR Mall, Mota Varachha, Surat - 394101.
                  </Typography>
                </Box>
              </ContactCard>

              <ContactCard>
                <Phone sx={{ color: PRIMARY_COLOR, fontSize: 32 }} />
                <Box>
                  <Typography fontWeight={600}>Phone</Typography>
                  <Typography variant="body2">+91-8141156734</Typography>
                   <Typography variant="body2">+91-9904870251</Typography>
                </Box>
              </ContactCard>

              <ContactCard>
                <Email sx={{ color: PRIMARY_COLOR, fontSize: 32 }} />
                <Box>
                  <Typography fontWeight={600}>Email</Typography>
                  <Typography variant="body2">
                    profitpilot2021@gmail.com
                  </Typography>
                </Box>
              </ContactCard>

              {/* <ContactCard>
                <Language sx={{ color: PRIMARY_COLOR, fontSize: 32 }} />
                <Box>
                  <Typography fontWeight={600}>Website</Typography>
                  <Typography variant="body2">www.pihantech.com</Typography>
                </Box>
              </ContactCard> */}
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <StyledForm onSubmit={formik.handleSubmit}>
              {" "}
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Send a Message
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  label="Your Name"
                  fullWidth
                  required
                  {...formik.getFieldProps("name")}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
                <TextField
                  label="Email Address"
                  type="email"
                  fullWidth
                  required
                  {...formik.getFieldProps("email")}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />
                <TextField
                  label="Phone Number"
                  fullWidth
                  required
                  {...formik.getFieldProps("mobile_no")}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.mobile_no && Boolean(formik.errors.mobile_no)}
                  helperText={formik.touched.mobile_no && formik.errors.mobile_no}
                />
                <TextField
                  label="Subject"
                  fullWidth
                  {...formik.getFieldProps("subject")}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <TextField
                  label="Message"
                  multiline
                  rows={4}
                  fullWidth
                  required
                  {...formik.getFieldProps("message")}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.message && Boolean(formik.errors.message)
                  }
                  helperText={formik.touched.message && formik.errors.message}
                />
                <StyledButton
                  type="submit"
                  variant="contained"
                >
                  Send Message
                </StyledButton>
              </Box>
            </StyledForm>

            <Snackbar
          open={formSubmitted}
          autoHideDuration={3000}
          onClose={() => setFormSubmitted(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setFormSubmitted(false)}
            severity="success"
            variant="filled"
            sx={{ width: "100%" }}
          >
            Feedback send succesfully..
          </Alert>
        </Snackbar>
          </Grid>
        </Grid>
      </SectionContainer>
      <Footer />
        
    </PageWrapper>
  );
}
